import moment from "moment";

/** Extrai data e horário do id de coluna da grade (formato TOTVS com `;`). */
export function parseSessionColumnId(columnId) {
  if (!columnId || typeof columnId !== "string") {
    return null;
  }
  const parts = columnId.split(";");
  if (parts.length < 8) {
    return null;
  }
  return {
    id: columnId,
    date: parts[parts.length - 1],
    startTime: parts[parts.length - 3],
    endTime: parts[parts.length - 2],
    idHorarioTurma: parts[2],
    idTurmaDisc: parts[1],
    codEtapa: parts[3]
  };
}

export function buildDoubleClassesByColumnId(headers) {
  const map = {};
  (headers || []).forEach(column => {
    if (column.double !== "true") {
      return;
    }
    const relatedIds = Array.isArray(column.doubleRelatedIds)
      ? column.doubleRelatedIds
      : [];
    const groupIds = new Set([column.id, ...relatedIds]);
    const group = (headers || []).filter(header => groupIds.has(header.id));
    if (group.length < 2) {
      return;
    }
    map[column.id] = group;
  });
  return map;
}

export function buildSessionColumnsMeta(headers) {
  return (headers || [])
    .map(header => {
      const parsed = parseSessionColumnId(header.id);
      if (!parsed) {
        return null;
      }
      return {
        ...parsed,
        date: header.date || parsed.date
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.date !== b.date) {
        return a.date.localeCompare(b.date);
      }
      return a.startTime.localeCompare(b.startTime);
    });
}

/**
 * Grupos aninhados do header da grade:
 * período (pai, já existente) → data (filho) → coluna com horário.
 * No ReactDataGrid, o grupo filho aponta `group` para o nome do pai.
 */
export function buildNestedFrequencyHeaderGroups(
  headers,
  periodGroups = [],
  locale = "pt-br"
) {
  const parents = (periodGroups || []).map((group, index) => ({
    ...group,
    name: group.name || group.header || `period-${index}`,
    header: group.header || group.name || `period-${index}`,
    order: group.order != null ? group.order : index
  }));
  const parentNames = new Set(parents.map(group => group.name));

  (headers || []).forEach(column => {
    const parentName = column.group || "Frequência";
    if (!parentNames.has(parentName)) {
      parents.push({
        name: parentName,
        header: parentName,
        order: parents.length
      });
      parentNames.add(parentName);
    }
  });

  const dateGroups = [];
  const seen = new Set();
  (headers || []).forEach(column => {
    const parsed = parseSessionColumnId(column.id);
    const date = column.date || (parsed && parsed.date);
    if (!date) {
      return;
    }
    const parentName = column.group || "Frequência";
    const name = `${parentName}::${date}`;
    if (seen.has(name)) {
      return;
    }
    seen.add(name);
    const day = moment(date);
    if (!day.isValid()) {
      return;
    }
    dateGroups.push({
      name,
      group: parentName,
      header: `${day.format("DD/MM")} · ${day.locale(locale).format("dddd")}`,
      order: dateGroups.length
    });
  });

  return { periodGroups: parents, dateGroups };
}

export function resolveFrequencyNestedDateGroup(column) {
  const parsed = parseSessionColumnId(column && column.id);
  const date = (column && column.date) || (parsed && parsed.date);
  const parentName = (column && column.group) || "Frequência";
  if (!date) {
    return parentName;
  }
  return `${parentName}::${date}`;
}

export function rowDataToMap(rowData) {
  if (!Array.isArray(rowData)) {
    return {};
  }
  return Object.fromEntries(
    rowData
      .filter(item => item && item.columnId)
      .map(item => [item.columnId, item])
  );
}

export function isCellAbsent(cell) {
  return !!cell && String(cell.absence) === "false";
}

/** Falta abonada pelo motivo de justificativa (`justificada = 1` no sistema escolar). */
export function isCellAbsenceExcused(cell) {
  return isCellAbsent(cell) && String(cell.justificada) === "1";
}

export function isCellAbsenceJustified(cell) {
  return (
    isCellAbsent(cell) &&
    (!!cell.idJustificativaFalta || String(cell.justificada || "") !== "")
  );
}

/** Aluno faltou em alguma aula anterior no mesmo dia da coluna informada. */
export function wasAbsentInEarlierSessionSameDay(rowData, columnId, sessionMeta) {
  const current = sessionMeta.find(session => session.id === columnId);
  if (!current) {
    return false;
  }

  const earlierSessions = sessionMeta.filter(
    session =>
      session.date === current.date &&
      session.startTime < current.startTime
  );
  if (!earlierSessions.length) {
    return false;
  }

  const rowMap = rowDataToMap(rowData);
  return earlierSessions.some(session => isCellAbsent(rowMap[session.id]));
}

/** Aluno faltou em aula anterior em algum dia com mais de um horário na grade. */
export function hasAbsentInEarlierSessionSameDay(rowData, sessionMeta) {
  const sessionsByDate = sessionMeta.reduce((acc, session) => {
    if (!acc[session.date]) {
      acc[session.date] = [];
    }
    acc[session.date].push(session);
    return acc;
  }, {});

  const rowMap = rowDataToMap(rowData);

  return Object.values(sessionsByDate).some(daySessions => {
    if (daySessions.length < 2) {
      return false;
    }
    const ordered = [...daySessions].sort((a, b) =>
      a.startTime.localeCompare(b.startTime)
    );
    return ordered
      .slice(0, -1)
      .some(session => isCellAbsent(rowMap[session.id]));
  });
}

function findGridRowForStudent(lines, student) {
  if (!Array.isArray(lines)) {
    return null;
  }
  return lines.find(line => {
    const control = line && line[0];
    if (!control) {
      return false;
    }
    const sid = String(control.studentId);
    return sid === String(student.ra) || sid === String(student.id);
  });
}

/** Localiza a coluna da grade correspondente ao horário de ocorrências. */
export function findCurrentSessionColumn(schedule, sessionMeta) {
  if (!schedule || !sessionMeta.length) {
    return null;
  }

  const dateStr = moment(schedule.date).format("YYYY-MM-DD");
  const byScheduleId = sessionMeta.find(
    session =>
      session.date === dateStr &&
      String(session.idHorarioTurma) === String(schedule.scheduleId)
  );
  if (byScheduleId) {
    return byScheduleId;
  }

  if (schedule.startTime) {
    return (
      sessionMeta.find(
        session =>
          session.date === dateStr && session.startTime === schedule.startTime
      ) || null
    );
  }

  return null;
}

/**
 * Marca alunos com falta na aula atual ou em horário anterior no mesmo dia
 * (cruzamento com GET gridFrequency).
 */
export function enrichOccurrenceStudentsWithDayAbsence(students, schedule, grid) {
  if (!Array.isArray(students)) {
    return [];
  }

  const sessionMeta = buildSessionColumnsMeta(grid && grid.headers);
  const currentColumn = findCurrentSessionColumn(schedule, sessionMeta);
  const lines = (grid && grid.lines) || [];

  return students.map(student => {
    let absentEarlierSameDay = false;
    let absentFromGridCurrent = false;

    if (currentColumn) {
      const row = findGridRowForStudent(lines, student);
      const rowData = row ? row.slice(1) : [];
      const rowMap = rowDataToMap(rowData);
      absentFromGridCurrent = isCellAbsent(rowMap[currentColumn.id]);
      absentEarlierSameDay = wasAbsentInEarlierSessionSameDay(
        rowData,
        currentColumn.id,
        sessionMeta
      );
    }

    const absentCurrentLesson = !!student.absence || absentFromGridCurrent;

    return {
      ...student,
      absence: absentCurrentLesson,
      absentEarlierSameDay,
      absentToday: absentCurrentLesson || absentEarlierSameDay
    };
  });
}

/**
 * Conta aulas em que o aluno está ausente (`absence === "false"`), sem as faltas abonadas.
 * Por columnId único.
 */
export function countAbsencesInFrequencyRow(data) {
  if (!Array.isArray(data)) {
    return 0;
  }
  const absentColumnIds = new Set();
  data.forEach(item => {
    if (
      item &&
      item.columnId &&
      item.columnId !== "studentId" &&
      isCellAbsent(item) &&
      !isCellAbsenceExcused(item)
    ) {
      absentColumnIds.add(item.columnId);
    }
  });
  return absentColumnIds.size;
}

/** Conta aulas do período selecionado na linha (células com presença/falta). */
export function countSessionsInFrequencyRow(data) {
  if (!Array.isArray(data)) {
    return 0;
  }
  const sessionColumnIds = new Set();
  data.forEach(item => {
    if (
      item &&
      item.columnId &&
      item.columnId !== "studentId" &&
      item.absence !== undefined
    ) {
      sessionColumnIds.add(item.columnId);
    }
  });
  return sessionColumnIds.size;
}

/** Percentual de faltas no período selecionado: faltas / aulas da grade × 100. */
export function absencePercentInFrequencyRow(data) {
  const sessions = countSessionsInFrequencyRow(data);
  if (sessions === 0) {
    return 0;
  }
  return Math.round((countAbsencesInFrequencyRow(data) / sessions) * 100);
}
